/* eslint-disable max-len */
import { call, put, takeLatest, select, putResolve } from 'redux-saga/effects';
import orderBy from 'lodash/orderBy.js';
import safeSaga from '../safeSaga';
import {
  UPDATE_UPLOAD_IMAGE_SUCCESS,
  UPDATE_UPLOAD_IMAGE,
  UPDATE_IMAGE_LOADING,
  UPDATE_UPLOAD_IMAGE_ERROR_INFO,
  UPDATE_LOGO_COLOR_COUNT,
} from '../../actions/actionTypes';
import { uploadImagestoS3 as uploadImagestoS3Api } from '../../api/uploadImageApi';
import { getLogoUsedColors } from '../../api/getLogoUsedColorsApi';
import { updateAddToBundleDisabled } from '../../actions/configAction';
import {
  trackSubObjectInteractions,
  trackErrorInteractions,
} from '../../utils/analytics/trackEvents';
import { getUploadImageChoiceValue } from '../../utils/uploadImagesUtil';
import { uploadImagesSelector, productDetailsSelector } from '../../selectors';
import { updatePath } from '../../actions/pathActions';
import { saveUpdatedLogoColorCount } from '../../actions/uploadImages';
import getExpConfig from '../../utils/getExpConfig';

const getUsedColors = (data) => {
  if (!data) return null;

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(data, 'text/xml');

  const usedColors = xmlDoc.querySelector('metadata[name=UsedColors]')?.textContent;

  const numberList = usedColors?.split(',');
  const propertyMap = {
    0: 'r',
    1: 'g',
    2: 'b',
    3: 'a',
    4: 'count',
  };

  const delimeter = 4;
  let current = 0;
  const colorList = [];
  let newColor = {};
  const buildNewColor = (obj, currentKey, item) => ({
    ...obj,
    [propertyMap[currentKey]]: parseInt(item, 10),
  });
  numberList.forEach((item) => {
    if (current === delimeter) {
      // c
      newColor = buildNewColor(newColor, current, item);
      // add new color to colorArr
      colorList.push(newColor);
      // reset current and new color
      newColor = {};
      current = 0;
    } else {
      // r,g,b,a
      newColor = buildNewColor(newColor, current, item);
      current += 1;
    }
  });

  let filteredList = orderBy(
    colorList.filter((item) => item.a === 0),
    'count',
    'desc'
  );

  filteredList = filteredList?.map((color, key) => ({ ...color, orderKey: key + 1 }));

  let notUsedFilteredList = orderBy(
    colorList.filter((item) => item.a !== 0),
    'count',
    'desc'
  );

  notUsedFilteredList = notUsedFilteredList?.map((color, key) => ({ ...color, orderKey: key + 1 }));

  const compiledColorData = {
    usedColors: filteredList,
    unusedColors: notUsedFilteredList,
    usedColorsCount: filteredList?.length || 0,
    unusedColorsCount: notUsedFilteredList?.length || 0,
  };

  return compiledColorData;
};

function* updateUploadImageSaga(action) {
  try {
    const { attributes } = yield select(productDetailsSelector);

    const params = {
      file: action.payload.file,
      lineItemId: action.payload.lineItemId,
      uploadType: `${getExpConfig('amazon-s3-environment-path')}Local`,
      logoPosition: action.payload.logoPosition,
      ppeFileType: action.payload.ppeFileType,
    };

    if (attributes?.ppeProductType === 'vest') {
      // need to send image to ppe
      // eslint-disable-next-line no-console
      console.log('vest get colorize version, then upload');
    }

    const response = yield call(uploadImagestoS3Api, params);
    if (response.status === 200) {
      const { uploadImagesUrl, uploadImages } = yield select(uploadImagesSelector);

      let payload = {
        fileUrl: response.data.url,
        id: action.payload.id,
        fileName: action.payload.fileName,
        showUploadRemoveButton: action.payload.showUploadRemoveButton,
        isImageLoading: false,
        uploadType: `${getExpConfig('amazon-s3-environment-path')}Local`,
        lineItemId: action.payload.lineItemId.split('-').join('_'),
        logoPosition: action.payload.logoPosition.toLowerCase(),
        ppeFileType: action.payload.ppeFileType.toLowerCase(),
        uploadImagesUrl,
      };
      const uploadImageSubContent = {
        section: 'upload images',
        component: 'upload image success',
        target: `${action.payload.logoPosition}|${action.payload.fileName}`,
      };
      trackSubObjectInteractions('interaction', 'pip sub-content', uploadImageSubContent);

      if (attributes?.ppeProductType === 'vest') {
        const logoPosition = action.payload.logoPosition.toLowerCase();
        const currentLogoProps = uploadImages.find(
          (image) => image.uploadImageTabName.toLowerCase() === logoPosition
        );
        const locationMap = { front: 'logo1', back: 'logo4' };
        // get image used colors count
        const logoRequest = locationMap[logoPosition];
        const colorUseParams = {
          path: 'https://homedepot.liquifire.com/homedepot?',
          printLogoChain: `set=showKey[0]&set=showLogo[${logoRequest}],type[print]&set=${logoRequest}[${
            response.data.url
          }],${logoRequest}BGremove[${
            currentLogoProps?.toggleRemoveBackground ? '1' : '0'
          }],${logoRequest}Colorize[1],${logoRequest}Pos[${
            currentLogoProps?.imagePlacement
          }],${logoRequest}Size[${currentLogoProps?.imageSize}]`,
          printCall: '&call=url[file:global.type]&sink=format[xml]',
        };

        const useColorsResponse = yield call(getLogoUsedColors, colorUseParams);
        const colorData = getUsedColors(useColorsResponse?.data);

        payload = {
          ...payload,
          maxColorsToUse: Number(attributes.ppeMaxColorsToUse),
          colorData: {
            ...colorData,
          },
        };
      }

      yield put({ type: UPDATE_UPLOAD_IMAGE_SUCCESS, payload });
      yield put(updateAddToBundleDisabled({ isAddtoBundleDisabled: false }));
      yield put(updatePath());
    } else {
      let errorMessage = response.response?.data.Message;
      if (
        response?.status === 500 ||
        response.message === 'Network Error' ||
        response?.status === 503
      ) {
        errorMessage = 'Sorry, something went wrong. Please try again.';
      }
      const errorObject = {
        type: 'system',
        displayType: 'inline',
        pageSection: 'Upload Image',
        message: errorMessage,
        code: response?.status,
      };
      trackErrorInteractions(errorObject, 'error', 'post page error');
      const payload = {
        id: action.payload.id,
        isImageLoading: false,
        uploadImageErrorMessage: errorMessage,
        showUploadImageError: true,
      };
      yield put({ type: UPDATE_IMAGE_LOADING, payload });
      yield put({ type: UPDATE_UPLOAD_IMAGE_ERROR_INFO, payload });
      if (getUploadImageChoiceValue) {
        yield put(updateAddToBundleDisabled({ isAddtoBundleDisabled: false }));
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
  }
}

function* updateLogoColorCount(action) {
  const { uploadImages } = yield select(uploadImagesSelector);

  const logoPosition = action.payload.logoPosition.toLowerCase();
  const currentLogoProps = uploadImages.find(
    (image) => image.uploadImageTabName.toLowerCase() === logoPosition
  );
  const locationMap = { front: 'logo1', back: 'logo4' };
  // get image used colors count
  const logoRequest = locationMap[logoPosition];

  const colorUseParams = {
    path: 'https://homedepot.liquifire.com/homedepot?',
    printLogoChain: `set=showKey[0]&set=showLogo[${logoRequest}],type[print]&set=${logoRequest}[${
      currentLogoProps.uploadedImageUrl
    }],${logoRequest}BGremove[${
      currentLogoProps?.toggleRemoveBackground ? 1 : 0
    }],${logoRequest}Colorize[1],${logoRequest}Pos[${
      currentLogoProps?.imagePlacement
    }],${logoRequest}Size[${currentLogoProps?.imageSize}]`,
    printCall: '&call=url[file:global.type]&sink=format[xml]',
  };

  const useColorsResponse = yield call(getLogoUsedColors, colorUseParams);
  const colorData = getUsedColors(useColorsResponse?.data);

  const { attributes } = yield select(productDetailsSelector);
  yield putResolve(
    saveUpdatedLogoColorCount({
      logoPosition,
      newColorCount:
        colorData.usedColorsCount > Number(attributes.ppeMaxColorsToUse)
          ? Number(attributes.ppeMaxColorsToUse)
          : colorData.usedColorsCount,
    })
  );
}

export default function* uploadImagesSaga() {
  yield takeLatest(UPDATE_UPLOAD_IMAGE, safeSaga(updateUploadImageSaga));
  yield takeLatest(UPDATE_LOGO_COLOR_COUNT, safeSaga(updateLogoColorCount));
}
